// store/financials/selectors.ts

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// ------------------------------------ BASE SELECTORS ------------------------------------

export const selectFinancials = (state: RootState) => state.financials;

// ------------------------------------ CORE DATA SELECTORS ------------------------------------

export const selectFinancialsData = (state: RootState) => state.financials.financialsData;

// ------------------------------------ LOADING SELECTORS ------------------------------------

export const selectFinancialsLoading = (state: RootState) => state.financials.isLoadingFinancials;

// ------------------------------------ ERROR SELECTORS ------------------------------------

export const selectFinancialsError = (state: RootState) => state.financials.financialsError;

// ------------------------------------ METADATA SELECTORS ------------------------------------

export const selectFinancialsCount = (state: RootState) => state.financials.financialsCount;
export const selectFinancialsLastUpdated = (state: RootState) => state.financials.lastUpdated;
export const selectLastFinancialsFilters = (state: RootState) =>
  state.financials.lastFinancialsFilters;

// ----------------------------------- DERIVED SELECTORS -----------------------------------

// Check if data is ready (not loading and no errors)
export const selectFinancialsReady = createSelector(
  [selectFinancialsLoading, selectFinancialsError],
  (isLoading, hasError) => !isLoading && !hasError
);

// ----------------------------------- FINANCIALS ANALYTICS -----------------------------------

// Get financials by company
export const selectFinancialsByCompany = createSelector(
  [selectFinancialsData, (state: RootState, companyName: string) => companyName],
  (financialsData, companyName) =>
    financialsData.filter(financial => financial.company_name === companyName)
);

// Get financials by region
export const selectFinancialsByRegion = createSelector(
  [selectFinancialsData, (state: RootState, regionName: string) => regionName],
  (financialsData, regionName) =>
    financialsData.filter(financial => financial.region_name === regionName)
);

// Get financials by industry
export const selectFinancialsByIndustry = createSelector(
  [selectFinancialsData, (state: RootState, industryName: string) => industryName],
  (financialsData, industryName) =>
    financialsData.filter(financial => financial.industry_name === industryName)
);

// Get financials by year
export const selectFinancialsByYear = createSelector(
  [selectFinancialsData, (state: RootState, year: number) => year],
  (financialsData, year) => financialsData.filter(financial => financial.year === year)
);

// Get financials by quarter
export const selectFinancialsByQuarter = createSelector(
  [selectFinancialsData, (state: RootState, quarter: number) => quarter],
  (financialsData, quarter) => financialsData.filter(financial => financial.quarter === quarter)
);

// Get financials by revenue range
export const selectFinancialsByRevenueRange = createSelector(
  [
    selectFinancialsData,
    (state: RootState, minRevenue: number) => minRevenue,
    (state: RootState, minRevenue: number, maxRevenue: number) => maxRevenue,
  ],
  (financialsData, minRevenue, maxRevenue) =>
    financialsData.filter(
      financial => financial.revenue >= minRevenue && financial.revenue <= maxRevenue
    )
);

// Get top companies by revenue
export const selectTopCompaniesByRevenue = createSelector(
  [selectFinancialsData, (state: RootState, limit: number) => limit],
  (financialsData, limit) =>
    [...financialsData].sort((a, b) => b.revenue - a.revenue).slice(0, limit)
);

// Get top companies by assets
export const selectTopCompaniesByAssets = createSelector(
  [selectFinancialsData, (state: RootState, limit: number) => limit],
  (financialsData, limit) => [...financialsData].sort((a, b) => b.assets - a.assets).slice(0, limit)
);

// Get top companies by capex
export const selectTopCompaniesByCapex = createSelector(
  [selectFinancialsData, (state: RootState, limit: number) => limit],
  (financialsData, limit) => [...financialsData].sort((a, b) => b.capex - a.capex).slice(0, limit)
);

// ----------------------------------- AGGREGATION SELECTORS -----------------------------------

// Calculate total revenue across all financials
export const selectTotalRevenue = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce((total, financial) => total + financial.revenue, 0)
);

// Calculate total assets across all financials
export const selectTotalAssets = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce((total, financial) => total + financial.assets, 0)
);

// Calculate total capex across all financials
export const selectTotalCapex = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce((total, financial) => total + financial.capex, 0)
);

// Get revenue by region
export const selectRevenueByRegion = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce(
    (acc, financial) => {
      const region = financial.region_name || 'Unknown';
      acc[region] = (acc[region] || 0) + financial.revenue;
      return acc;
    },
    {} as Record<string, number>
  )
);

// Get revenue by industry
export const selectRevenueByIndustry = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce(
    (acc, financial) => {
      const industry = financial.industry_name || 'Unknown';
      acc[industry] = (acc[industry] || 0) + financial.revenue;
      return acc;
    },
    {} as Record<string, number>
  )
);

// Get revenue by year
export const selectRevenueByYear = createSelector([selectFinancialsData], financialsData =>
  financialsData.reduce(
    (acc, financial) => {
      const year = financial.year.toString();
      acc[year] = (acc[year] || 0) + financial.revenue;
      return acc;
    },
    {} as Record<string, number>
  )
);

// Get revenue by quarter for a specific year
export const selectRevenueByQuarterForYear = createSelector(
  [selectFinancialsData, (state: RootState, year: number) => year],
  (financialsData, year) => {
    const yearData = financialsData.filter(financial => financial.year === year);
    return yearData.reduce(
      (acc, financial) => {
        const quarter = `Q${financial.quarter}`;
        acc[quarter] = (acc[quarter] || 0) + financial.revenue;
        return acc;
      },
      {} as Record<string, number>
    );
  }
);

// ----------------------------------- STATISTICS SELECTORS -----------------------------------

// Calculate financials statistics
export const selectFinancialsStats = createSelector([selectFinancialsData], financialsData => {
  if (financialsData.length === 0) {
    return {
      count: 0,
      avgRevenue: 0,
      avgAssets: 0,
      avgCapex: 0,
      totalRevenue: 0,
      totalAssets: 0,
      totalCapex: 0,
    };
  }

  const revenues = financialsData.map(f => f.revenue);
  const assets = financialsData.map(f => f.assets);
  const capex = financialsData.map(f => f.capex);

  return {
    count: financialsData.length,
    totalRevenue: revenues.reduce((sum, rev) => sum + rev, 0),
    totalAssets: assets.reduce((sum, asset) => sum + asset, 0),
    totalCapex: capex.reduce((sum, cap) => sum + cap, 0),
    avgRevenue: revenues.reduce((sum, rev) => sum + rev, 0) / revenues.length,
    avgAssets: assets.reduce((sum, asset) => sum + asset, 0) / assets.length,
    avgCapex: capex.reduce((sum, cap) => sum + cap, 0) / capex.length,
    minRevenue: Math.min(...revenues),
    maxRevenue: Math.max(...revenues),
    minAssets: Math.min(...assets),
    maxAssets: Math.max(...assets),
    minCapex: Math.min(...capex),
    maxCapex: Math.max(...capex),
  };
});

// Get company count by region
export const selectCompanyCountByRegion = createSelector([selectFinancialsData], financialsData => {
  const uniqueCompanies = new Map();

  financialsData.forEach(financial => {
    const key = `${financial.company_name}-${financial.region_name}`;
    if (!uniqueCompanies.has(key)) {
      uniqueCompanies.set(key, financial.region_name || 'Unknown');
    }
  });

  return Array.from(uniqueCompanies.values()).reduce(
    (acc, region) => {
      acc[region] = (acc[region] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );
});

// Get company count by industry
export const selectCompanyCountByIndustry = createSelector(
  [selectFinancialsData],
  financialsData => {
    const uniqueCompanies = new Map();

    financialsData.forEach(financial => {
      const key = `${financial.company_name}-${financial.industry_name}`;
      if (!uniqueCompanies.has(key)) {
        uniqueCompanies.set(key, financial.industry_name || 'Unknown');
      }
    });

    return Array.from(uniqueCompanies.values()).reduce(
      (acc, industry) => {
        acc[industry] = (acc[industry] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }
);

// ----------------------------------- UTILITY SELECTORS -----------------------------------

// Get financials summary
export const selectFinancialsSummary = createSelector(
  [selectFinancialsStats, selectFinancialsLastUpdated],
  (financialsStats, lastUpdated) => ({
    financials: {
      count: financialsStats.count,
      totalRevenue: financialsStats.totalRevenue,
      totalAssets: financialsStats.totalAssets,
      totalCapex: financialsStats.totalCapex,
      avgRevenue: financialsStats.avgRevenue,
    },
    lastUpdated,
  })
);
